import { type ComponentType } from 'react';
import { type ColumnElementType } from '../../types';
import { useColumn } from '../columns/column-context';
import { columnElementsExistenceCheckMap } from '../constants';
import { BAR_HEIGHT } from '../highlight-elements';
import { useLayout } from '../layouts/layout-context';
import { useTables } from '../tables-context';
import { Button } from './button';
import { Features } from './features';
import { Picture } from './picture';
import { Price } from './price';
import { Title } from './title';

export interface ColumnElement {
  $alignRows: boolean;
  $columnIndex: number;
  $rowIndex: number;
  $additionalTopPadding?: number;
  $isLastElement?: boolean;
  $withElementClass?: boolean;
  className?: string;
}

const componentsMap: Record<ColumnElementType, ComponentType<ColumnElement>> = {
  button: Button,
  title: Title,
  features: Features,
  picture: Picture,
  price: Price
};

type ColumnElementsProps = Omit<ColumnElement, '$rowIndex'>;

export function ColumnElements({
  $alignRows: alignRows,
  $columnIndex: columnIndex,
  ...forwardingProps
}: ColumnElementsProps) {
  const { elements, hasBarHighlight } = useTables();
  const { rowIndexes } = useLayout();
  const column = useColumn();

  const { highlightLabel } = column;
  const derivedHasBarHighlight = alignRows
    ? hasBarHighlight
    : !!highlightLabel && hasBarHighlight;

  let actualIndex = 0;
  return elements.map((type) => {
    const checkIfElementExists = columnElementsExistenceCheckMap[type];
    if (!checkIfElementExists(column)) {
      return null;
    }

    const ColumnElement = componentsMap[type];

    actualIndex += 1;
    const derivedRowIndex = alignRows ? rowIndexes[type] + 1 : actualIndex;

    const additionalTopPadding =
      derivedHasBarHighlight && derivedRowIndex === 1 && type !== 'picture'
        ? BAR_HEIGHT
        : undefined;

    return (
      <ColumnElement
        key={`element-${type}-${columnIndex}-${derivedRowIndex}`}
        $alignRows={alignRows}
        $columnIndex={columnIndex}
        $rowIndex={derivedRowIndex}
        $isLastElement={derivedRowIndex === elements.length}
        $additionalTopPadding={additionalTopPadding}
        {...forwardingProps}
      />
    );
  });
}
